export const ACCOUNT_TYPES = ['SUPPLY_LOGIC', 'SUPPLIER', 'CLIENT'] as const
export type AccountType = (typeof ACCOUNT_TYPES)[number]

export const ROLES = ['DESIGNER', 'EDITOR', 'REVIEWER', 'APPROVER'] as const
export type Role = (typeof ROLES)[number]

/** Every account type has the same four roles. Account administration is a separate flag (`isAccountAdmin`). */
export const ROLES_BY_ACCOUNT_TYPE: Record<AccountType, readonly Role[]> = {
  SUPPLY_LOGIC: ROLES,
  SUPPLIER: ROLES,
  CLIENT: ROLES,
}

/** Designers and editors build: they create projects, generate and edit designs, and choose who reviews. */
export const isBuilderRole = (role: Role) => role === 'DESIGNER' || role === 'EDITOR'

/** Someone with a `role` and, when they hold several, the whole `roles` list. Older records only have `role`. */
type WithRoles = { role: Role; roles?: readonly Role[] }

/** Every working role the person holds. */
export const rolesOf = (user: WithRoles): readonly Role[] => (user.roles?.length ? user.roles : [user.role])
export const hasRole = (user: WithRoles, role: Role) => rolesOf(user).includes(role)
/** A builder if ANY of their roles builds. */
export const isBuilder = (user: WithRoles) => rolesOf(user).some(isBuilderRole)


export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  SUPPLY_LOGIC: 'SupplyLogic',
  SUPPLIER: 'Supplier',
  CLIENT: 'Client',
}

export const ROLE_LABEL: Record<Role, string> = {
  DESIGNER: 'Designer',
  EDITOR: 'Editor',
  REVIEWER: 'Reviewer',
  APPROVER: 'Approver',
}

/** "Designer, Approver": all of a person's roles in words. */
export const roleLabels = (user: WithRoles) => rolesOf(user).map((role) => ROLE_LABEL[role]).join(', ')

/** Core.Accounts */
export interface Account {
  id: string
  accountName: string
  accountType: AccountType
  /** A small preview (data URL) or a path. Null when no logo was uploaded. */
  logoUrl: string | null
  createdAt: string
  /** Contact details and status, as the backend holds them. */
  email?: string
  phoneNumber?: string
  address?: Address | null
  isActive?: boolean
}

/** A postal address. Optional as a whole; when one is given, street, city, postal code and country are all required. */
export interface Address {
  line1: string
  line2: string
  city: string
  region: string
  postalCode: string
  country: string
}

export const blankAddress: Address = { line1: '', line2: '', city: '', region: '', postalCode: '', country: '' }

export const hasAddress = (address: Partial<Address> | null | undefined): address is Address => Object.values(address ?? {}).some((part) => part?.trim())

/** One line, e.g. "12 Harbour Rd, Austin, TX 78701, United States". Empty when there is no address. */
export function formatAddress(address: Partial<Address> | null | undefined): string {
  if (!hasAddress(address)) return ''
  const cityLine = [address.city, [address.region, address.postalCode].filter((part) => part?.trim()).join(' ')].filter((part) => part?.trim()).join(', ')
  return [address.line1, address.line2, cityLine, address.country].map((part) => part?.trim()).filter(Boolean).join(', ')
}

/** Core.Users */
export interface User {
  id: string
  accountId: string
  firstName: string
  lastName: string
  email: string
  /** The main working role, for places that show just one (tones, avatars). All of them are in `roles`. */
  role: Role
  /** Every working role (DESIGNER, EDITOR, REVIEWER, APPROVER) the person holds. */
  roles?: Role[]
  /** May manage the account's users. Separate from the role, which decides the design work. */
  isAccountAdmin: boolean
  phoneNumber?: string
  isActive?: boolean
  createdAt: string
}

/**
 * Core.AccountRelationships: who sits under whom. SupplyLogic is above every supplier and above the clients it manages
 * directly; a supplier is above its own clients. `childAccountId` is unique: every account but SupplyLogic has exactly
 * one parent, so a client either belongs to a supplier or is managed directly by SupplyLogic.
 */
export type RelationshipType = 'SUPPLY_LOGIC_SUPPLIER' | 'SUPPLY_LOGIC_CLIENT' | 'SUPPLIER_CLIENT'

export interface AccountRelationship {
  id: string
  parentAccountId: string
  childAccountId: string
  relationshipType: RelationshipType
  createdAt: string
}

/** The signed-in user as the screens need them. */
export interface CurrentUser extends User {
  fullName: string
  account: Account
}

/** What a role may do. Mirrors `roles` in the seed. */
export interface RoleCapabilities {
  projects: 'ALL' | 'ASSIGNED' | 'LINKED_CLIENTS' | 'OWN_CLIENT'
  users: 'ALL' | 'OWN_ACCOUNT' | 'NONE'
  createAccountTypes?: AccountType[]
  generate: boolean
  approve: boolean
}

export const fullName = (user: Pick<User, 'firstName' | 'lastName'>) => `${user.firstName} ${user.lastName}`.trim()
